/**
 * ReportsView (SK-REPORTS, T-22, DS-1g-05 §5.1, PIN-1h→1g-OUTBOX-CONTRACT)
 * — AC-1g-18.
 *
 * §7.M7 reconciliation to `Mark PWA Preview.dc.html`: a list⇄detail
 * VIEW-SWAP (mirroring the Master List / Decision surfaces). LIST = title +
 * Daily/Weekly filter pills + tappable report rows (title, meta, draft/sent
 * pill). DETAIL = "‹ Reports" BackLink + meta (weather auto-fill line on
 * daily) + the "Included items — flagged at save" list (each item tappable →
 * the caller's report deep-link into the shared save screen, keyed by
 * `capture_id`) + View + Approve-and-send. Client-facing sends ALWAYS take the
 * rule-#7 second confirm (§4 rule 7 / AC-1g-17); internal daily sends go
 * straight through (auto-draft, human-approved — §4 rule 8). The old flat
 * View/Save/Share debug row is retired.
 *
 * Consumes the PUBLISHED outbox READ contract's `list_reports()` shape
 * (`outbox-read-contract.v1.md` §2, `ReportEntry`) as an already-listed,
 * already RBAC-scoped `ReportEntry[]` handed in by the caller — this
 * component has NO read-transport opinion (matching DecisionLogView/T-18
 * and MasterActionListView/T-19's convention; LIVE read transport is
 * GATED per PIN-1g→1l-PWA-TRANSPORT/S8). It is also Lock 1 of AC-1g-28
 * (T-16): the ONLY inputs here are a typed `ReportEntry[]` and the
 * caller-supplied fetchers — no raw-row read path exists in this file.
 *
 * The report SIDECAR (the 1i ReportModel JSON — included items, weather,
 * approval state) is deliberately OFF `ReportEntry`: `outbox-read-contract.v1.md`
 * §2 governs keys/pairing/listing/provenance but "NOT sidecar internals",
 * so `ReportEntry` stays a pure mirror of that contract and the sidecar
 * arrives through its OWN gated fetch seam (`fetchSidecar`), exactly like
 * `fetchPdfBytes` supplies the PDF — fixture-backed in Stage A, the real
 * 1i/1h-lite client once the live leg lands.
 *
 * "NO render logic in the PWA (render stays 1i; storage 1h-lite)" — this
 * component never inspects/re-encodes PDF bytes; it only ever fetches the
 * artifact via `fetchPdfBytes` and hands the SAME bytes straight through to
 * `onView`/`onSend` — the byte-identical guarantee (AC-1g-18) holds because
 * nothing in this file ever mutates them.
 */

import { FiledReportDetail } from "./FiledReportDetail";
import { ReportEntryDetail } from "./ReportEntryDetail";
import { ReportPreview } from "./ReportPreview";
import { ReportsList } from "./ReportsList";
import { useReportsViewState } from "./reports-view-state";
import type { FileableWindow, FiledReport } from "../reports/report-client";
import type {
  ReportEntry,
  ReportIncludedItem,
  ReportKind,
  ReportSidecar,
} from "../reports/report-types";

export type ReportFilter = "all" | "daily" | "weekly" | "client";

export interface ReportsViewProps {
  reports: ReportEntry[];
  /** #19: re-file an ALREADY-filed window, from that report's own detail screen.
   * Handed the filed report so the caller derives `as_of` from the window it was
   * filed FOR — re-filing Tuesday's daily must produce Tuesday's daily, and
   * deriving from today's clock would quietly file a different one. */
  onRefile?: (report: FiledReport) => Promise<boolean>;
  /** Download the durable PDF export for an already-filed report. */
  onDownload?: (report: FiledReport) => void;
  /** A1: shown when a disabled client-report control is tapped. Same seam the
   * SaveScreen and decision-detail chips already use, so all three say the same
   * thing from one constant. */
  onComingSoon?: (message: string) => void;
  /** Resolves a `pdf_key` to the artifact's raw bytes — no PWA
   * storage-transport opinion (fixture-backed in Stage A). */
  fetchPdfBytes: (pdf_key: string) => Promise<Uint8Array>;
  /** Resolves a `sidecar_key` to the 1i ReportModel content (included items,
   * weather, sent). Optional so the fixture-only AC-1g-18 byte tests can omit
   * it; when absent the detail simply shows no included items. */
  fetchSidecar?: (sidecar_key: string) => Promise<ReportSidecar>;
  /** Resolves an ALREADY-FILED report to what went into it. Distinct from
   * `fetchSidecar`, which is keyed by a `sidecar_key` only an unfiled
   * `ReportEntry` carries — a filed PDF is identified by its bucket + filename,
   * which is all the folder listing knows. `null` means "could not read it",
   * never "it was empty". */
  fetchFiledSidecar?: (report: FiledReport) => Promise<ReportSidecar | null>;
  /** Resolves a window that has NOT been filed to what WOULD go into it. Same
   * projection the filing call runs, minus the emit — so a preview can never
   * disagree with the report it previews. `null` means "could not read it", never
   * "it would be empty". */
  fetchWindowPreview?: (
    w: FileableWindow,
    signal?: AbortSignal,
    includeEligible?: boolean,
  ) => Promise<ReportSidecar | null>;
  /** Lazily resolves an authenticated, server-held photo for an opened report. */
  fetchPhoto?: (media_ref: string) => Promise<Blob | null>;
  /** Changes when local delivery state changes. An open preview re-projects so
   * a pin that just reached the server cannot leave a stale snapshot on screen. */
  previewRefreshKey?: string;
  /** Deep-link an included item to the shared save screen (saveFrom:"report"). */
  onOpenItem?: (capture_id: string) => void;
  /** Explicit report curation. Resolves only after the membership correction is durable. */
  onSetItemIncluded?: (
    capture_id: string,
    report_kind: ReportKind,
    included: boolean,
    signal?: AbortSignal,
    window?: FileableWindow,
    item?: ReportIncludedItem,
  ) => Promise<boolean>;
  /** Approve-and-send — handed BYTE-IDENTICAL bytes (AC-1g-18), only AFTER the
   * rule-#7 second confirm for client-facing reports. */
  onSend?: (entry: ReportEntry, bytes: Uint8Array) => void;
  /** §align: selected project's visible name — appended to the list subtitle. */
  projectLabel?: string;
  /** Stable project authority used to invalidate async report reads on switch. */
  projectKey?: string;
  /** #15: the windows a human can file RIGHT NOW (today's daily, this week's
   * weekly + client), derived from the clock. Deliberately not `ReportEntry` —
   * that type describes an artifact that EXISTS, and these do not yet. */
  fileable?: FileableWindow[];
  /** #15: what has already been filed, read from the deliverable folder. */
  filed?: FiledReport[];
  filedStatus?: "loading" | "ready" | "error";
  onFileWindow?: (w: FileableWindow) => void;
}

export function ReportsView({
  reports,
  fetchPdfBytes,
  fetchSidecar,
  fetchFiledSidecar,
  fetchWindowPreview,
  fetchPhoto,
  previewRefreshKey,
  onOpenItem,
  onSetItemIncluded,
  onSend,
  onRefile,
  onDownload,
  onComingSoon,
  projectLabel,
  projectKey,
  fileable = [],
  filed = [],
  filedStatus = "ready",
  onFileWindow,
}: ReportsViewProps) {
  const {
    detail,
    confirmOpen,
    confirmWindow,
    setConfirmWindow,
    filter,
    setFilter,
    sidecar,
    sidecarError,
    setConfirmOpen,
    previewWindow,
    refileTarget,
    curationBusy,
    curationError,
    removedItems,
    selectedAdditions,
    setSelectedAdditions,
    rootRef,
    filtered,
    fileableInFilter,
    fileableShown,
    filedShown,
    filedDetail,
    openDetail,
    closeDetail,
    openFiledDetail,
    closeFiledDetail,
    openPreview,
    closePreview,
    setItemIncluded,
    addSelectedItems,
    doSend,
    onSendClick,
    openRefilePreview,
    approvePreview,
  } = useReportsViewState({
    reports,
    fetchPdfBytes,
    fetchSidecar,
    fetchFiledSidecar,
    fetchWindowPreview,
    previewRefreshKey,
    onSetItemIncluded,
    onSend,
    onRefile,
    onComingSoon,
    projectKey,
    fileable,
    filed,
    filedStatus,
    onFileWindow,
  });

  return (
    <section ref={rootRef} data-skin-slot="SK-REPORTS" data-testid="reports-view">
      {previewWindow !== null ? (
        <ReportPreview
          previewWindow={previewWindow}
          refileTarget={refileTarget}
          sidecar={sidecar}
          sidecarError={sidecarError}
          curationBusy={curationBusy}
          curationError={curationError}
          removedItems={removedItems}
          selectedAdditions={selectedAdditions}
          setSelectedAdditions={setSelectedAdditions}
          confirmWindow={confirmWindow}
          setConfirmWindow={setConfirmWindow}
          closePreview={closePreview}
          setItemIncluded={setItemIncluded}
          addSelectedItems={addSelectedItems}
          approvePreview={approvePreview}
          fetchPhoto={fetchPhoto}
          onOpenItem={onOpenItem}
          onSetItemIncluded={onSetItemIncluded}
          onComingSoon={onComingSoon}
        />
      ) : filedDetail !== null ? (
        <FiledReportDetail
          filedDetail={filedDetail}
          sidecar={sidecar}
          sidecarError={sidecarError}
          closeFiledDetail={closeFiledDetail}
          openRefilePreview={openRefilePreview}
          fetchPhoto={fetchPhoto}
          onOpenItem={onOpenItem}
          onDownload={onDownload}
        />
      ) : detail === null ? (
        <ReportsList
          filter={filter}
          setFilter={setFilter}
          projectLabel={projectLabel}
          filedStatus={filedStatus}
          fileableShown={fileableShown}
          fileableInFilter={fileableInFilter}
          filedShown={filedShown}
          filtered={filtered}
          confirmWindow={confirmWindow}
          setConfirmWindow={setConfirmWindow}
          openPreview={openPreview}
          openFiledDetail={openFiledDetail}
          openDetail={openDetail}
          onComingSoon={onComingSoon}
          onFileWindow={onFileWindow}
        />
      ) : (
        <ReportEntryDetail
          detail={detail}
          sidecar={sidecar}
          confirmOpen={confirmOpen}
          setConfirmOpen={setConfirmOpen}
          closeDetail={closeDetail}
          doSend={doSend}
          onSendClick={onSendClick}
          onOpenItem={onOpenItem}
        />
      )}
    </section>
  );
}
